import { Box, Button, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Role, Specialty } from 'shared/types';

import { RoleAutocomplete, SpecialtyAutocomplete } from '../entitySearch';

interface AddRoleSpecialtyPanelProps {
    isEditing: boolean;
    role: Role | null;
    specialty: Specialty | null;
    onRoleChange: (role: Role | null) => void;
    onSpecialtyChange: (specialty: Specialty | null) => void;
    onClose: () => void;
    disabled?: boolean;
}

/**
 * Controlled inline editor for a single (role, speciality) entry. Changes are
 * applied to the list immediately by the parent — there is no separate "add"
 * confirmation to forget; the only button just closes the editor.
 */
export default function AddRoleSpecialtyPanel({
    isEditing,
    role,
    specialty,
    onRoleChange,
    onSpecialtyChange,
    onClose,
    disabled
}: AddRoleSpecialtyPanelProps) {
    const { t } = useTranslation('rolesSpecialtiesSelector');

    return (
        <PanelContainer>
            <Typography variant="subtitle1">
                { t(isEditing ? 'editPanelTitle' : 'addPanelTitle') }
            </Typography>
            <Stack gap={ 2 }>
                <RoleAutocomplete
                    value={ role }
                    onChange={ onRoleChange }
                    placeholder={ t('rolePlaceholder') }
                    // Fetch suggestions only when the dropdown is opened, not every time
                    // the editor is shown (e.g. when just viewing/editing an existing entry).
                    preloadOnMount={ false }
                    preloadOnOpen
                    disabled={ disabled }
                />
                <SpecialtyAutocomplete
                    // Remount on role change so the next open preloads the new role's scope
                    // (useServerSearch preloads once per mount).
                    key={ role?.id ?? 'none' }
                    roleId={ role?.id ?? null }
                    value={ specialty }
                    onChange={ onSpecialtyChange }
                    placeholder={ t('specialtyPlaceholder') }
                    preloadOnMount={ false }
                    preloadOnOpen
                    disabled={ disabled }
                />
            </Stack>
            <Stack direction="row"
                gap={ 2 }
                sx={{ justifyContent: 'flex-end' }}
            >
                <Button variant="contained"
                    color="primary"
                    onClick={ onClose }
                    disabled={ disabled }
                >
                    { t('done') }
                </Button>
            </Stack>
        </PanelContainer>
    );
}

const PanelContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${ theme.palette.divider }`
}));
